import { ChangeDetectionStrategy, Component, Input, computed, inject, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MenuStyleService } from 'src/app/services/menu-style.service';
import { LL_ICONS } from './ll-icon-data';

/**
 * One icon for the new menu surfaces (dock, rail, More sheet, header). Give it the PrimeIcons string the server menu already sends
 * ("pi pi-fw pi-truck"); it draws the matching Lucide or Phosphor artwork for the person's chosen icon set, and falls back to the
 * PrimeIcons font when the set is "classic" or the icon has no artwork yet. So a new icon on the server can never show as a blank.
 */
@Component({
  selector: 'll-icon',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (view().html; as html) {
      <span class="ll-icon-art" aria-hidden="true" [innerHTML]="html"></span>
    } @else {
      <i [class]="view().pi" aria-hidden="true"></i>
    }
  `,
  styles: [`
    :host { display: inline-flex; align-items: center; justify-content: center; width: 1.25em; height: 1.25em; flex: none; line-height: 1; }
    .ll-icon-art { display: inline-flex; width: 100%; height: 100%; }
    :host ::ng-deep .ll-icon-art svg { width: 100%; height: 100%; display: block; }
  `],
})
export class LlIconComponent {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly menuStyle = inject(MenuStyleService);
  private readonly nameSig = signal('');

  /** The PrimeIcons string ("pi pi-fw pi-truck") or just the name ("truck"). */
  @Input() set name(value: string | null | undefined) {
    this.nameSig.set((value ?? '').trim());
  }

  readonly view = computed<{ html: SafeHtml | null; pi: string }>(() => {
    const name = this.nameSig();
    const key = LlIconComponent.keyOf(name);
    const pi = name.includes('pi-') ? name : `pi pi-${name}`;
    const set = this.menuStyle.iconSet();
    const art = LL_ICONS[key];
    if (set === 'classic' || !art) return { html: null, pi };
    // The artwork is our own static data (see ll-icon-data.ts), never user input, so trusting it is safe.
    const svg =
      set === 'phosphor'
        ? `<svg viewBox="0 0 256 256" fill="currentColor">${art.p}</svg>`
        : `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">${art.l}</svg>`;
    return { html: this.sanitizer.bypassSecurityTrustHtml(svg), pi };
  });

  /** "pi pi-fw pi-truck" -> "truck". */
  private static keyOf(name: string): string {
    const tokens = name.split(/\s+/).filter((t) => t.startsWith('pi-') && t !== 'pi-fw');
    return tokens.length ? tokens[0].slice(3) : name;
  }
}
